/* eslint-disable default-case */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

const SearchSelect = ({
 options = [],
 value = '',
 onChange = () => {},
 onSearch = () => {},
 placeholder = 'Select an option...',
 searchPlaceholder = 'Search...',
 label = '',
 error = '',
 disabled = false,
 required = false,
 className = '',
 size = 'md',
 variant = 'default',
 multiple = false,
 clearable = false,
 searchable = true,
 loading = false,
 emptyMessage = 'No options found',
 ...props
}) => {
 const [isOpen, setIsOpen] = useState(false);
 const [searchTerm, setSearchTerm] = useState('');
 const [highlightedIndex, setHighlightedIndex] = useState(-1);
 const [dropdownPosition, setDropdownPosition] = useState('bottom');
 const dropdownRef = useRef(null);
 const searchRef = useRef(null);
 const listboxIdRef = useRef(`search-select-listbox-${Math.random().toString(36).slice(2, 10)}`);

 // Calculate dropdown position
 useEffect(() => {
 if (isOpen && dropdownRef.current) {
 const rect = dropdownRef.current.getBoundingClientRect();
 const viewportHeight = window.innerHeight;
 const spaceBelow = viewportHeight - rect.bottom;
 const spaceAbove = rect.top;
 const dropdownHeight = 240; // Approximate height of dropdown
 
 if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
 setDropdownPosition('top');
 } else {
 setDropdownPosition('bottom');
 }
 }
 }, [isOpen]);

 const baseClasses = 'box-border flex w-full items-center border border-border rounded-md shadow-none text-ink focus:outline-none focus:ring-1 focus:ring-ink focus:border-ink transition-colors duration-150';
 
 const sizeClasses = {
 sm: 'h-7 min-h-7 px-2.5 text-xs leading-none',
 md: 'h-8 min-h-8 px-3 text-xs leading-none',
 lg: 'h-9 min-h-9 px-3 text-sm leading-none',
 };

 const variantClasses = {
 default: 'bg-surface',
 filled: 'bg-surface-muted',
 error: 'border-red-300 focus:ring-red-500 focus:border-red-500'
 };

 const inputClasses = `
 ${baseClasses}
 ${sizeClasses[size] || sizeClasses.md}
 ${variantClasses[error ? 'error' : variant]}
 ${disabled ? 'bg-surface-muted cursor-not-allowed opacity-60' : 'cursor-pointer'}
 ${className}
 `.trim();

 // Filter options based on search term
 const filteredOptions = options.filter(option => {
 if (!searchable || !searchTerm) return true;
 const searchValue = typeof option === 'string' ? option : option.label || option.name;
 return searchValue.toLowerCase().includes(searchTerm.toLowerCase());
 });

 // Handle option selection
 const handleSelect = (option) => {
 if (multiple) {
 const optionValue = typeof option === 'string' ? option : option.value;
 const currentValues = Array.isArray(value) ? value : [];
 
 if (currentValues.includes(optionValue)) {
 onChange(currentValues.filter(v => v !== optionValue));
 } else {
 onChange([...currentValues, optionValue]);
 }
 } else {
 const optionValue = typeof option === 'string' ? option : option.value;
 onChange(optionValue);
 setIsOpen(false);
 setSearchTerm('');
 }
 };

 // Handle clear selection
 const handleClear = (e) => {
 e.stopPropagation();
 onChange(multiple ? [] : '');
 setSearchTerm('');
 };

 // Handle keyboard navigation
 const handleKeyDown = (e) => {
 if (!isOpen) {
 if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
 e.preventDefault();
 setIsOpen(true);
 if (searchable && searchRef.current) {
 searchRef.current.focus();
 }
 }
 return;
 }

 switch (e.key) {
 case 'ArrowDown':
 e.preventDefault();
 setHighlightedIndex(prev => 
 prev < filteredOptions.length - 1 ? prev + 1 : 0
 );
 break;
 case 'ArrowUp':
 e.preventDefault();
 setHighlightedIndex(prev => 
 prev > 0 ? prev - 1 : filteredOptions.length - 1
 );
 break;
 case 'Enter':
 e.preventDefault();
 if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
 handleSelect(filteredOptions[highlightedIndex]);
 }
 break;
 case 'Escape':
 setIsOpen(false);
 setSearchTerm('');
 setHighlightedIndex(-1);
 break;
 }
 };

 // Close dropdown when clicking outside
 useEffect(() => {
 const handleClickOutside = (event) => {
 if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
 setIsOpen(false);
 setSearchTerm('');
 setHighlightedIndex(-1);
 }
 };

 document.addEventListener('mousedown', handleClickOutside);
 return () => document.removeEventListener('mousedown', handleClickOutside);
 }, []);

 // Focus search input when dropdown opens
 useEffect(() => {
 if (isOpen && searchable && searchRef.current) {
 searchRef.current.focus();
 }
 }, [isOpen, searchable]);

 // Get display value
 const getDisplayValue = () => {
 if (multiple) {
 if (!Array.isArray(value) || value.length === 0) return placeholder;
 if (value.length === 1) {
 const option = options.find(opt => 
 (typeof opt === 'string' ? opt : opt.value) === value[0]
 );
 return typeof option === 'string' ? option : option?.label || option?.name;
 }
 return `${value.length} selected`;
 }

 if (!value) return placeholder;
 const option = options.find(opt => 
 (typeof opt === 'string' ? opt : opt.value) === value
 );
 return typeof option === 'string' ? option : option?.label || option?.name;
 };

 // Check if option is selected
 const isSelected = (option) => {
 const optionValue = typeof option === 'string' ? option : option.value;
 if (multiple) {
 return Array.isArray(value) && value.includes(optionValue);
 }
 return value === optionValue;
 };

 return (
 <div className="w-full">
 {label && (
 <label className="mb-1.5 block text-xs font-medium text-ink sm:mb-2 sm:text-sm">
 {label}
 {required && <span className="text-red-500 ml-1">*</span>}
 </label>
 )}
 
 <div className="relative" ref={dropdownRef}>
 <div
 className={inputClasses}
 onClick={() => !disabled && setIsOpen(!isOpen)}
 onKeyDown={handleKeyDown}
 tabIndex={disabled ? -1 : 0}
 role="combobox"
 aria-expanded={isOpen}
 aria-haspopup="listbox"
 aria-controls={listboxIdRef.current}
 >
 <div className="flex h-full w-full items-center justify-between gap-2">
 <span className={`truncate ${!value || (multiple && Array.isArray(value) && value.length === 0) ? 'text-ink-tertiary' : 'text-ink'}`}>
 {getDisplayValue()}
 </span>
 <div className="flex items-center gap-1.5">
 {clearable && value && (
 <button
 type="button"
 onClick={handleClear}
 className="text-ink-tertiary hover:text-ink"
 >
 <X className="h-3.5 w-3.5" />
 </button>
 )}
 <ChevronDown className={`h-3.5 w-3.5 text-ink-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
 </div>
 </div>
 </div>

 {isOpen && (
 <div
 id={listboxIdRef.current}
 className={`absolute z-50 w-full max-h-60 overflow-hidden rounded-md border border-border bg-surface shadow-sm ${ dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1' }`}
 >
 {searchable && (
 <div className="border-b border-border p-2">
 <div className="relative">
 <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
 <input
 ref={searchRef}
 type="text"
 placeholder={searchPlaceholder}
 value={searchTerm}
 onChange={(e) => {
 setSearchTerm(e.target.value);
 onSearch(e.target.value);
 }}
 className="box-border h-8 min-h-8 w-full rounded-md border border-border bg-surface pl-8 pr-3 text-xs leading-none text-ink placeholder:text-ink-tertiary focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink"
 />
 </div>
 </div>
 )}

 <div className="max-h-48 overflow-y-auto py-1">
 {loading ? (
 <div className="p-3 text-center text-ink-secondary">
 <div className="mx-auto h-5 w-5 animate-spin rounded-full border-b-2 border-ink"></div>
 <p className="mt-2 text-xs">Loading...</p>
 </div>
 ) : filteredOptions.length === 0 ? (
 <div className="p-3 text-center text-xs text-ink-secondary">
 {emptyMessage}
 </div>
 ) : (
 filteredOptions.map((option, index) => {
 const optionValue = typeof option === 'string' ? option : option.value;
 const optionLabel = typeof option === 'string' ? option : option.label || option.name;
 const isOptionSelected = isSelected(option);
 const isHighlighted = index === highlightedIndex;

 return (
 <div
 key={optionValue}
 className={`flex cursor-pointer items-center justify-between px-3 py-1.5 text-xs transition-colors ${ isOptionSelected ? 'bg-ink text-white' : isHighlighted ? 'bg-surface-muted text-ink' : 'text-ink hover:bg-surface-muted' }`}
 onClick={() => handleSelect(option)}
 onMouseEnter={() => setHighlightedIndex(index)}
 >
 <span>{optionLabel}</span>
 {isOptionSelected && (
 <Check className="h-3.5 w-3.5 flex-shrink-0" />
 )}
 </div>
 );
 })
 )}
 </div>
 </div>
 )}
 </div>
 
 {error && (
 <p className="mt-1 text-xs text-red-600 sm:text-sm">{error}</p>
 )}
 </div>
 );
};

export default SearchSelect;
